/** Klaro consent storage key - distinct from tour_completed, mobile-warning-dismissed, and ?s= share state. */
export const KLARO_STORAGE_NAME = 'klaro';

export interface KlaroServiceConfig {
  name: string;
  title?: string;
  description?: string;
  purposes: string[];
  default?: boolean;
  required?: boolean;
  cookies?: (string | RegExp)[];
  callback?: (consent: boolean, service: KlaroServiceConfig) => void;
}

export interface KlaroConfig {
  version: number;
  elementID: string;
  storageMethod: 'localStorage' | 'cookie';
  storageName: string;
  cookieName: string;
  default: boolean;
  mustConsent: boolean;
  acceptAll: boolean;
  hideDeclineAll: boolean;
  hideLearnMore: boolean;
  disablePoweredBy?: boolean;
  htmlTexts?: boolean;
  groupByPurpose: boolean;
  purposes: string[];
  purposeOrder: string[];
  styling: { theme: string[] };
  translations: Record<string, Record<string, unknown>>;
  services: KlaroServiceConfig[];
  callback?: (consent: boolean, service: KlaroServiceConfig) => void;
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/** Map Klaro service consent states to GTM Consent Mode v2. */
export function syncGtmConsentFromKlaro(consents: Record<string, boolean | undefined>): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  const analyticsGranted = consents.googleAnalytics === true;
  const adsGranted = consents.googleAdsense === true;

  window.gtag('consent', 'update', {
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
    ad_storage: adsGranted ? 'granted' : 'denied',
    ad_personalization: adsGranted ? 'granted' : 'denied',
    ad_user_data: adsGranted ? 'granted' : 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });
}

/** Snapshot updated by per-service callbacks. Do not call getManager() here - it re-enters ConsentManager construction and overflows the stack. */
const consentSnapshot: Record<string, boolean | undefined> = {};

function recordServiceConsent(consent: boolean, serviceName: string): void {
  consentSnapshot[serviceName] = consent;
  syncGtmConsentFromKlaro(consentSnapshot);
}

export const klaroConfig: KlaroConfig = {
  version: 1,
  elementID: 'klaro',
  storageMethod: 'localStorage',
  storageName: KLARO_STORAGE_NAME,
  cookieName: KLARO_STORAGE_NAME,
  default: false,
  mustConsent: false,
  acceptAll: true,
  hideDeclineAll: false,
  hideLearnMore: false,
  disablePoweredBy: true,
  htmlTexts: true,
  groupByPurpose: true,
  purposes: ['analytics', 'advertising', 'functional'],
  purposeOrder: ['functional', 'analytics', 'advertising'],
  styling: {
    theme: ['light', 'bottom'],
  },
  translations: {
    en: {
      acceptAll: 'Accept all',
      acceptSelected: 'Save preferences',
      ok: 'Accept all',
      save: 'Save preferences',
      decline: 'Decline optional cookies',
      consentNotice: {
        title: 'Cookie preferences',
        description:
          'Your mortgage calculations never leave your browser. We use optional cookies for analytics and advertising - you can accept, decline, or choose individually.',
        learnMore: 'Manage preferences',
      },
      consentModal: {
        title: 'Cookie preferences',
        description:
          'Choose which optional services to enable. Calculator state, tour progress, and form security are always on and cannot be turned off. See our <a href="/privacy-policy">privacy policy</a> for details.',
      },
      purposes: {
        analytics: {
          title: 'Analytics',
          description:
            'Helps us understand how visitors use the site in aggregate. No mortgage figures or personal inputs are included.',
        },
        advertising: {
          title: 'Advertising',
          description:
            'Enables ads and ad measurement when our advertising partner is active.',
        },
        functional: {
          title: 'Essential functionality (always on)',
          description:
            'Required for core site features, including form spam protection and saving your calculator scenarios.',
        },
      },
      googleAnalytics: {
        title: 'Google Analytics (GA4)',
        description:
          'Collects anonymous usage data via Google Tag Manager. Your mortgage amounts and inputs are never sent.',
      },
      googleAdsense: {
        title: 'Google AdSense',
        description:
          'Will display ads once our AdSense account is approved. Listed here now so you can set your preference in advance.',
      },
      recaptcha: {
        title: 'Google reCAPTCHA',
        description:
          'Protects contact and feedback forms from spam. Always enabled; not subject to this preference.',
      },
      service: {
        disableAll: {
          title: 'Toggle all optional services',
        },
      },
    },
  },
  services: [
    {
      name: 'recaptcha',
      title: 'Google reCAPTCHA',
      purposes: ['functional'],
      required: true,
      default: true,
    },
    {
      name: 'googleAnalytics',
      title: 'Google Analytics (GA4)',
      purposes: ['analytics'],
      default: false,
      required: false,
      cookies: [/^(_ga|_gid|_gat)(_.+)?$/],
      callback: (consent) => {
        recordServiceConsent(consent, 'googleAnalytics');
      },
    },
    {
      name: 'googleAdsense',
      title: 'Google AdSense',
      purposes: ['advertising'],
      default: false,
      required: false,
      cookies: [],
      callback: (consent) => {
        recordServiceConsent(consent, 'googleAdsense');
      },
    },
  ],
};
